import type {AttributeValue} from './attribute'
import type {SetAttributeRequest} from './attribute-definition'
import type {Instant, UUID} from './common'
import type {EntityLink} from './entity-link'
import type {OwnerResponse} from './owner'
/**
 * Attribute response from the API.
 * Shared between resource, party, and function group attributes.
 */
export interface EntityAttribute extends AttributeValue {
    version: number;
}

/**
 * Narrow list-item shape returned by list endpoints — mirrors backend
 * {@code ResourceListItem} (1120.05). The list page reads only these fields;
 * detail / write endpoints return the wider {@link Resource} shape that
 * extends this one.
 */
export interface ResourceListItem {
    id: UUID;
    targetSystem: EntityLink;
    resourceType: string;
    nativeKey: string;
    derivedDisplayName: string;
    dataClassification: string | null;
    createdAt: Instant;
}

/**
 * Wide resource response from detail / write endpoints — mirrors backend
 * {@code ResourceDetail extends ResourceListItem}. The legacy fat shape was
 * renamed to keep frontend imports stable; consumers that need only the
 * narrow projection should reference {@link ResourceListItem} instead.
 */
export interface Resource extends ResourceListItem {
    parent: EntityLink | null;
    childCount: number;
    hasChildren: boolean;
    attributes: EntityAttribute[] | null;
    version: number;
    updatedAt: Instant;
}

/**
 * Request payload for POST /api/i/resources (issue 744).
 */
export interface ResourceCreateRequest {
    targetSystemId: UUID;
    resourceType: string;
    nativeKey: string;
    displayName: string;
    dataClassification?: string | null;
    parentId?: UUID | null;
    attributes: SetAttributeRequest[];
}

/**
 * Request payload for PUT /api/i/resources/{id} (issue 873).
 * Per-field edit dialog sends the new displayName + loaded version.
 */
export interface ResourceUpdateRequest {
    displayName: string;
    version: number;
}

/**
 * Resource tree node from the tree endpoint.
 */
export interface ResourceTreeNode {
    resource: EntityLink;
    dataClassification: string;
    depth: number;
    children: ResourceTreeNode[];
}

/**
 * Resource ancestor from the ancestors endpoint.
 */
export interface ResourceAncestor {
    resource: EntityLink;
}

/**
 * Resource summary for embedding in other responses.
 */
export interface ResourceSummary {
    id: UUID;
    name: string;
    resourceType: string;
}

/**
 * Resource owner response from the API.
 */
export interface ResourceOwner extends OwnerResponse {
    resourceId: UUID;
}

/**
 * Paginated response for resources.
 */
export interface ResourcePage {
    content: Resource[];
    totalElements: number;
    totalPages: number;
    number: number;
    size: number;
}

export interface AssignOwnerRequest {
    ownerPartyId: UUID;
    responsibility: string;
}

export interface SetParentRequest {
    parentResourceId: UUID;
    version: number;
}

/* eslint-disable i18next/no-literal-string -- field name constants for backend API */
export const AssignOwnerRequestFields = {
    ownerPartyId: 'ownerPartyId',
    responsibility: 'responsibility',
} as const

export const ResourceAncestorResponseFields = {
    resource: 'resource',
} as const

export const ResourceOwnerResponseFields = {
    id: 'id',
    resourceId: 'resourceId',
    ownerPartyId: 'ownerPartyId',
    ownerPartyName: 'ownerPartyName',
    responsibility: 'responsibility',
    validFrom: 'validFrom',
    validTo: 'validTo',
    isOpenEnded: 'isOpenEnded',
    isActive: 'isActive',
    version: 'version',
} as const

export const ResourceResponseFields = {
    id: 'id',
    targetSystem: 'targetSystem',
    resourceType: 'resourceType',
    nativeKey: 'nativeKey',
    derivedDisplayName: 'derivedDisplayName',
    dataClassification: 'dataClassification',
    parent: 'parent',
    childCount: 'childCount',
    hasChildren: 'hasChildren',
    attributes: 'attributes',
    version: 'version',
    createdAt: 'createdAt',
    updatedAt: 'updatedAt',
} as const

export const ResourceTreeNodeResponseFields = {
    resource: 'resource',
    dataClassification: 'dataClassification',
    depth: 'depth',
    children: 'children',
} as const

export const SetParentRequestFields = {
    parentResourceId: 'parentResourceId',
    version: 'version',
} as const

export const ResourceFilterParams = {
    resources: 'resources',
} as const
/* eslint-enable i18next/no-literal-string */
